import { SECTION_IDS } from '../lib/links';
import consultationDiagram from '../assets/v2-consultation.png';

/**
 * "Schedule a Consultation" — Figma node 2:39.
 *
 * Replaces the previous "Deploy the Orbis Intelligence Platform" band, reusing
 * the same icon-cluster illustration and the same two-column shape.
 *
 * Design values (at the 1920 reference width):
 *  · row      gap-112, items-center; illustration 703×468; text column w-841 gap-26
 *  · heading  Inter 700 48/48, tracking -1px, #202939 (= landing-slate)
 *  · copy     Inter 400 18/24, #697586 (= landing-pale), CSS `capitalize`
 *  · button   bg #E87722 (= primary-500), px-30 py-17, r-8, Inter 500 18px white
 *
 * Note the heading is `text-center` in the file but sits in an `items-start`
 * column, so it shrinks to its content and reads as left-aligned — the same
 * quirk as the reports heading. Reproduced as left-aligned.
 */
export function ScheduleConsultation() {
  return (
    <section
      id={SECTION_IDS.consultation}
      className="w-full scroll-mt-16 bg-white px-5 py-12 sm:px-6 md:px-8 lg:scroll-mt-24 lg:px-10 lg:py-16 xl:px-16 2xl:px-[132px]"
    >
      <div className="mx-auto flex max-w-[1656px] flex-col items-center gap-8 lg:flex-row lg:gap-16 xl:gap-[112px]">
        <img
          src={consultationDiagram}
          alt="Diagram of Orbis connecting infrastructure sources to risk, dependency and compliance outputs"
          loading="lazy"
          decoding="async"
          width={703}
          height={468}
          className="h-auto w-full max-w-[560px] shrink-0 lg:w-[48%] lg:max-w-[703px]"
        />

        <div className="flex w-full flex-col items-start gap-5 lg:min-w-0 lg:flex-1 lg:gap-[26px]">
          <h2 className="m-0 text-[28px] font-bold leading-[1.2] tracking-[-0.6px] text-landing-slate sm:text-[34px] lg:text-[40px] lg:tracking-[-1px] 2xl:text-5xl 2xl:leading-[48px]">
            Schedule a Consultation
          </h2>

          <p className="m-0 text-base capitalize leading-6 text-landing-pale lg:text-lg">
            Our experts can help you determine the right approach for your specific challenges.
            Whether you&apos;re dealing with aging systems or complex M&amp;A integration,
            we&apos;ll recommend the optimal strategy.
          </p>

          {/*
            Was `DEMO_MAILTO_URL` (opens the visitor's mail client with a
            pre-filled subject). Now routes into the /qualifier funnel — the
            6-question form captures a lead into HubSpot via /api/qualifier-lead
            instead of the mailto: dead-drop. Button text is deliberately kept
            as "Schedule a Consultation" — the qualifier ends with a pre-sales
            follow-up, which is effectively the same promise.
          */}
          <a
            href="/qualifier"
            className="flex w-full items-center justify-center rounded-lg bg-primary-500 px-[30px] py-[17px] text-lg font-medium text-white transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 sm:w-auto"
          >
            Schedule a Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
